import React, { useState, useRef, useEffect } from 'react';
import { ArrowDownIcon, ArrowUpIcon } from '@heroicons/react/24/solid';

import Spinner from '../moduleComponents/SpinnerLoad';
import ScrollbarNavigation from './viewComponents/scrollbar_navigation';
import CardTrip from './viewComponents/card_trip';
import DrawerTripDetails from './viewComponents/DrawerTripDetails';
import FabTripsMenu from './viewComponents/FabTripsMenu';

const TabTrips = ({ trips, isTripsLoading, fetchTrips }) => {
  const [sortDescending, setSortDescending] = useState(true);
  const [isFullWidth, setIsFullWidth] = useState(false);
  const containerRef = useRef(null);

  /** undefined = closed; else { intent, trip? } */
  const [drawerEntry, setDrawerEntry] = useState(undefined);

  const handleOpenTrip = (trip) =>
    setDrawerEntry({ intent: 'view', trip });

  const handleCreateTrip = () =>
    setDrawerEntry({ intent: 'create-trip', trip: null });

  const sortedTrips = [...trips].sort((a, b) => {
    const dateA = new Date(a.start_date);
    const dateB = new Date(b.start_date);
    return sortDescending ? dateB - dateA : dateA - dateB;
  });

  const tripsByYear = sortedTrips.reduce((groups, trip) => {
    const year = new Date(trip.start_date).getFullYear();
    if (!groups[year]) groups[year] = [];
    groups[year].push(trip);
    return groups;
  }, {});

  const years = Object.keys(tripsByYear).sort((a, b) =>
    sortDescending ? b - a : a - b
  );

  useEffect(() => {
    const checkFullWidth = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        setIsFullWidth(containerWidth >= window.innerWidth - 32);
      }
    };
    checkFullWidth();
    window.addEventListener('resize', checkFullWidth);
    return () => window.removeEventListener('resize', checkFullWidth);
  }, []);

  return (
    <>
      <div className="bg-white rounded-2xl shadow flex items-center justify-between h-12 m-2 px-4">
        <span className="text-lg font-semibold text-gray-900">Trips</span>
        <button
          type="button"
          onClick={() => setSortDescending((prev) => !prev)}
          className="flex items-center gap-1 text-sm text-gray-600 hover:text-black"
        >
          {sortDescending ? (
            <>
              <ArrowDownIcon className="h-4 w-4" />
              Newer
            </>
          ) : (
            <>
              <ArrowUpIcon className="h-4 w-4" />
              Older
            </>
          )}
        </button>
      </div>

      {isTripsLoading ? (
        <Spinner />
      ) : trips.length === 0 ? (
        <p className="text-gray-500 px-2">No trips registered yet.</p>
      ) : (
        <div className="relative">
          <ScrollbarNavigation
            years={years}
            tripsByYear={tripsByYear}
            sortDescending={sortDescending}
            containerRef={containerRef}
            isFullWidth={isFullWidth}
          />
          <div
            ref={containerRef}
            className="max-h-[calc(100vh-200px)] overflow-y-auto hide-scrollbar px-2"
          >
            {years.map((year) => (
              <div key={year} className="mb-6" data-year={year}>
                <div className="flex items-center mb-3 px-2">
                  <div className="flex-1 h-px bg-gray-200" />
                  <div className="px-3 py-1 bg-gray-100 rounded-full">
                    <span className="text-sm font-medium text-gray-600">{year}</span>
                  </div>
                  <div className="flex-1 h-px bg-gray-200" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {tripsByYear[year].map((trip) => (
                    <CardTrip
                      key={trip.id}
                      trip={trip}
                      onClick={() => handleOpenTrip(trip)}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <DrawerTripDetails
        isOpen={drawerEntry !== undefined}
        entry={drawerEntry}
        onClose={() => setDrawerEntry(undefined)}
        onTripsChanged={fetchTrips}
      />

      <FabTripsMenu onNewTrip={handleCreateTrip} />
    </>
  );
};

export default TabTrips;
